import { Order, Route, RouteAddress } from "../../types"
import { ChangeType, PendingMove, TimeDomain } from "./types"

// antd's translucent-black Tooltip bg washes out over this timeline's colored pills.
export const TOOLTIP_COLOR = "#1f1f1f"

// Local palette, intentionally not shared with older tags elsewhere (e.g. the
// "Recolher Caixa da Carrinha" tag still uses antd's stock "purple").
export const PICKUP_COLOR = "#7c3aed"
export const MOVED_COLOR = "#d97706"
export const REPOSITIONED_COLOR = "#2563eb"
export const GROUPED_COLOR = "#0d9488"

// on_transit is still allowed (driver's already out) — Routes/index.tsx's
// handleToggleTimeline warns before letting the user touch it.
const MOVE_ORDERS_INELIGIBLE_STATUSES = ["finished"]

// Today or a future date — only an already-elapsed day is off-limits.
export const isRouteEligibleForMoveOrders = (route: Route): boolean => {
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const routeDate = new Date(route.date)
	routeDate.setHours(0, 0, 0, 0)
	const isNotPast = routeDate.getTime() >= today.getTime()
	return isNotPast && !MOVE_ORDERS_INELIGIBLE_STATUSES.includes(route.status)
}

// Same "X" box-id convention as ContentTable.tsx — it's the box, not the orderId.
export const isOutOfFormatOrder = (order: Order): boolean =>
	(order.boxes || []).some((box) => box.id.startsWith("X"))

export const DND_TYPE = "route-stop"

// Layout
export const SIDEBAR_WIDTH = 200 // route name/driver column
export const ROW_HEIGHT = 60 // one route's timeline row

// Panel auto-sizes to route count, clamped so a huge selection scrolls instead
// of taking over the page.
export const TIMELINE_CHROME_HEIGHT = 120 // header + legend + panel padding
export const TICKS_RULER_HEIGHT = 24
export const MIN_TIMELINE_HEIGHT = 120
export const MAX_TIMELINE_HEIGHT = 640

// Horizontal zoom (1x fits the panel width). Opens at DEFAULT_ZOOM, not
// MIN_ZOOM, so it doesn't start on the whole-day/15-30min-tick view.
export const MIN_ZOOM = 1
export const MAX_ZOOM = 20
export const DEFAULT_ZOOM = 2

// Multiplicative so each click/notch is the same relative change at any zoom.
export const ZOOM_BUTTON_FACTOR = 1.35 // ~10 clicks MIN_ZOOM -> MAX_ZOOM
export const WHEEL_ZOOM_FACTOR = 1.05 // per wheel notch

// Below this zoom, pills collapse to just the sequence number (no orderId).
export const COMPACT_ZOOM_THRESHOLD = 2

// HTML5 drag doesn't auto-scroll, so a route scrolled out of view is
// otherwise unreachable mid-drag.
export const DRAG_SCROLL_EDGE_PX = 60
export const DRAG_SCROLL_MAX_SPEED = 16

export const FIVE_MINUTES = 5 * 60

// Breathing room before/after the first/last stop, so edge stops don't sit
// right on the track boundary.
export const TIME_DOMAIN_PADDING = 10 * 60

// Shared by the live and optimize-preview timelines for consistent rounding.
export const buildTimeDomain = (etas: number[]): TimeDomain | null => {
	if (etas.length === 0) return null
	const start =
		Math.floor(Math.min(...etas) / FIVE_MINUTES) * FIVE_MINUTES -
		TIME_DOMAIN_PADDING
	const end =
		Math.ceil(Math.max(...etas) / FIVE_MINUTES) * FIVE_MINUTES +
		TIME_DOMAIN_PADDING
	return { start, range: Math.max(end - start, FIVE_MINUTES) }
}

// Baseline tick step by zoom level, not by pixel gap — reproducible across
// panel widths, unlike a pixel target. First match wins, scanned in order.
const TICK_STEP_BREAKPOINTS: [number, number][] = [
	[1.5, 30],
	[3, 10],
	[6, 5],
	[10, 2],
	[Infinity, 1],
]
const baselineTickStepForZoom = (zoom: number): number =>
	TICK_STEP_BREAKPOINTS.find(([zoomBelow]) => zoom < zoomBelow)?.[1] ?? 1

const NICE_STEP_MINUTES = [1, 2, 5, 10, 15, 30, 60, 120]
// Min px between tick centers before a "07:54"-sized label starts overlapping.
const MIN_TICK_LABEL_GAP_PX = 50

// Escalates the zoom baseline to the next nice step if it's still too fine
// for the real pixel width (a long day on a narrow panel).
export const tickStepMinutes = (
	zoom: number,
	pixelsPerMinute: number
): number => {
	const baseline = baselineTickStepForZoom(zoom)
	return (
		NICE_STEP_MINUTES.find(
			(step) =>
				step >= baseline && step * pixelsPerMinute >= MIN_TICK_LABEL_GAP_PX
		) ?? NICE_STEP_MINUTES[NICE_STEP_MINUTES.length - 1]
	)
}

// Pixel margin (not percent) so a pill centered at 0%/100% doesn't clip under
// the sticky sidebar at low zoom — converted to percent per-track, below.
export const EDGE_INSET_PX = 56
const insetPercentFor = (trackWidth: number) =>
	(EDGE_INSET_PX / trackWidth) * 100
export const toInsetPercent = (percent: number, trackWidth: number) => {
	const inset = insetPercentFor(trackWidth)
	return inset + (percent / 100) * (100 - inset * 2)
}
// Inverse of toInsetPercent (e.g. turning a drop position back into a time).
export const fromInsetPercent = (
	insetedPercent: number,
	trackWidth: number
) => {
	const inset = insetPercentFor(trackWidth)
	return ((insetedPercent - inset) / (100 - inset * 2)) * 100
}

// "moved": from another route. "repositioned": reordered within its own route.
export const changeTypeFor = (
	orderId: string,
	pendingMoves: Map<string, PendingMove>,
	positionOverrides: Record<string, number>
): ChangeType =>
	pendingMoves.has(orderId)
		? "moved"
		: orderId in positionOverrides
			? "repositioned"
			: "none"

// Non-depot delivery stops are the only ones that can be moved between routes.
export const deliveryStops = (route?: Route): RouteAddress[] =>
	(route?.addresses || []).filter(
		(addr) => !addr.isDepot && addr.order?.orderId
	)

// Intermediate PICKUP stops (no orderId, so deliveryStops skips them) —
// rendered as a fixed, read-only marker like the depot.
export const pickupStops = (route?: Route): RouteAddress[] =>
	(route?.addresses || []).filter(
		(addr) => addr.stopType?.toUpperCase() === "PICKUP"
	)

// Route color → a translucent tint of it, for subtle route-colored
// backgrounds (optimize preview panels/rows) without a hardcoded palette.
export const tint = (hex: string, alpha: number): string => {
	const h = hex.replace("#", "")
	const r = parseInt(h.substring(0, 2), 16)
	const g = parseInt(h.substring(2, 4), 16)
	const b = parseInt(h.substring(4, 6), 16)
	return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const formatClock = (unixSeconds?: number): string => {
	if (!unixSeconds) return "-"
	const date = new Date(unixSeconds * 1000)
	return `${date.getHours().toString().padStart(2, "0")}:${date
		.getMinutes()
		.toString()
		.padStart(2, "0")}`
}
